/**
 * What a reader is told when something could not be read from the API.
 *
 * One sentence per kind of failure, the same on every page that loads a list
 * or an entry, so the same failure never reads two ways. None of them blames
 * the reader, and none of them says "not found" about a server that did not
 * answer — that was the entry page's old wording for every failure.
 *
 * `what` is the subject of the sentence: "Spells", "This spell".
 */
export function loadFailureText(err, what) {
  if (!err || !err.response) {
    return `${what} could not be loaded: the site did not answer. Check your connection, then try again.`;
  }
  if (err.response.status === 429) {
    return `${what} could not be loaded: too many requests from this connection in the last minute. Wait a minute, then try again.`;
  }
  return `${what} could not be loaded just now. Try again in a moment.`;
}

export default loadFailureText;

/**
 * The same sentences for an action (a save, a create, a delete), or null when
 * the server refused it for a reason of its own (a 4xx other than 401 and 429),
 * whose words the caller shows instead.
 *
 * A 5xx's body is the server's own shorthand ("Database error"), and a 502 from
 * the proxy has no JSON at all, so neither is ever shown as the reason: the
 * reader learns it did not go through and that trying again is the way on.
 */
export function actionFailureText(err) {
  if (!err || !err.response) return 'That did not go through: the site did not answer. Check your connection, then try again.';
  const { status } = err.response;
  if (status === 401) return 'That did not go through: you have been signed out. Sign in again, then try again.';
  if (status === 429) return 'That did not go through: too many requests from this connection in the last minute. Wait a minute, then try again.';
  if (status >= 500) return 'That did not go through just now. Try again in a moment.';
  return null;
}

/** True when the answer means "there is no such thing for you" (404, 403), not a failure to answer. */
export const isNotFound = (err) => {
  const status = err && err.response && err.response.status;
  return status === 404 || status === 403;
};

/**
 * Why a sign-in or sign-up did not go through when it was not the reader's
 * details (a 429 from the ten-a-minute sign-in limiter, or no answer), or null.
 * Trying again at once after a 429 only meets the limiter again, so it says to wait.
 */
export function signInFailureText(err) {
  if (!err || !err.response) return 'The site did not answer. Check your connection, then try again.';
  if (err.response.status === 429) return 'Too many sign-in attempts from this connection in the last minute. Wait a minute, then try again.';
  return null;
}
